// src/pages/salas/SalasHome.jsx — agenda del día de las salas de juntas.
//
// Una columna por sala, filas por intervalo (settings.slotMinutes). Tocar un
// hueco abre el formulario con esa sala/hora; tocar una reserva abre el
// detalle (editar / mover / cancelar según permisos). Los empalmes los
// rechaza el servidor (409) y aquí solo se muestra el motivo.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';
import socket from '../../services/socket';
import { useSalas, I } from './SalasShell';

// ── Fechas (todo en hora local del navegador = hora de CDMX para corporativo) ──
const pad = (n) => String(n).padStart(2, '0');
const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => toStr(new Date());
const fromStr = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d, 12); };
const addDays = (s, n) => { const d = fromStr(s); d.setDate(d.getDate() + n); return toStr(d); };
const hm = (d) => `${pad(new Date(d).getHours())}:${pad(new Date(d).getMinutes())}`;
const mins = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const fromMins = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const toDate = (dateStr, hhmm) => { const [y, mo, d] = dateStr.split('-').map(Number); const [h, mi] = hhmm.split(':').map(Number); return new Date(y, mo - 1, d, h, mi); };
const fmtLong = (s) => fromStr(s).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
const fmtShort = (d) => new Date(d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function useSlots(settings) {
    return useMemo(() => {
        if (!settings) return { slots: [], openMin: 0, closeMin: 0 };
        const openMin = mins(settings.openTime), closeMin = mins(settings.closeTime);
        const slots = [];
        for (let m = openMin; m < closeMin; m += settings.slotMinutes) slots.push(m);
        return { slots, openMin, closeMin };
    }, [settings]);
}

export default function SalasHome() {
    const { me, rooms, settings, access } = useSalas();
    const [params, setParams] = useSearchParams();
    const [date, setDate] = useState(() => params.get('fecha') || todayStr());
    const [bookings, setBookings] = useState([]);
    const [mine, setMine] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeRoom, setActiveRoom] = useState(() => {
        const k = params.get('sala');
        return (rooms.find(r => r.key === k) || rooms[0])?._id || null;
    });
    const [modal, setModal] = useState(null); // { type:'form', initial } | { type:'detail', booking }
    const [now, setNow] = useState(new Date());
    const { slots, openMin, closeMin } = useSlots(settings);

    useEffect(() => { if (!activeRoom && rooms[0]) setActiveRoom(rooms[0]._id); }, [rooms, activeRoom]);
    useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get('/salas/bookings', { params: { from: date, to: date } }),
                api.get('/salas/bookings/mine'),
            ]);
            setBookings(a.data); setMine(b.data);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cargar la agenda');
        } finally { setLoading(false); }
    }, [date]);

    useEffect(() => { load(); }, [load]);

    // Cambios de otros usuarios → refrescar en silencio (con pequeño debounce)
    const timer = useRef(null);
    useEffect(() => {
        const onChange = () => { clearTimeout(timer.current); timer.current = setTimeout(() => load(true), 300); };
        socket.on('salasChanged', onChange);
        return () => { socket.off('salasChanged', onChange); clearTimeout(timer.current); };
    }, [load]);

    const goDate = (s) => {
        setDate(s);
        const next = new URLSearchParams(params);
        if (s === todayStr()) next.delete('fecha'); else next.set('fecha', s);
        setParams(next, { replace: true });
    };

    const isToday    = date === todayStr();
    const isPastDay  = date < todayStr();
    const dayOpen    = settings?.weekdays?.includes(fromStr(date).getDay());
    const maxDate    = addDays(todayStr(), settings?.maxDaysAhead || 60);
    const nowMin     = now.getHours() * 60 + now.getMinutes();
    const total      = closeMin - openMin;
    const pct        = (m) => `${Math.max(0, Math.min(100, ((m - openMin) / total) * 100))}%`;

    const byRoom = useMemo(() => {
        const m = new Map(rooms.map(r => [String(r._id), []]));
        for (const b of bookings) {
            const k = String(b.room?._id || b.room);
            if (m.has(k)) m.get(k).push(b);
        }
        return m;
    }, [bookings, rooms]);

    const openForm = (roomId, startMin) => {
        if (!dayOpen || isPastDay) return;
        let s = startMin;
        if (isToday && s + settings.slotMinutes <= nowMin) return; // hueco ya pasado
        const defaultLen = Math.max(settings.slotMinutes, 60);
        // No pasar de la siguiente reserva de esa sala
        const nextStart = (byRoom.get(String(roomId)) || [])
            .map(b => { const d = new Date(b.start); return d.getHours() * 60 + d.getMinutes(); })
            .filter(m => m >= s + settings.slotMinutes)
            .reduce((min, m) => Math.min(min, m), closeMin);
        const e = Math.min(nextStart, s + defaultLen);
        setModal({ type: 'form', initial: { room: roomId, date, start: fromMins(s), end: fromMins(e) } });
    };
    const openNew = () => {
        // Siguiente hueco disponible: hoy → redondear hacia arriba al intervalo; otro día → apertura
        let s = openMin;
        if (isToday) s = Math.max(openMin, Math.ceil(nowMin / settings.slotMinutes) * settings.slotMinutes);
        if (s >= closeMin) s = openMin;
        setModal({ type: 'form', initial: { room: activeRoom || rooms[0]?._id, date: isPastDay ? todayStr() : date, start: fromMins(s), end: fromMins(Math.min(closeMin, s + 60)) } });
    };

    return (
        <>
            <div className="sl-toolbar">
                <div className="sl-datebox">
                    <button className="sl-btn sl-btn-ghost icon" onClick={() => goDate(addDays(date, -1))} aria-label="Día anterior">{I.left}</button>
                    <button className="sl-btn sl-btn-ghost icon" onClick={() => goDate(addDays(date, 1))} aria-label="Día siguiente">{I.right}</button>
                    <button className="sl-btn sl-btn-ghost sm" onClick={() => goDate(todayStr())} disabled={isToday}>Hoy</button>
                </div>
                <div>
                    <div className="sl-date-label">{fmtLong(date)}</div>
                    <div className="sl-date-sub">{settings.openTime} – {settings.closeTime} · {rooms.length} salas</div>
                </div>
                <input type="date" className="sl-input auto" value={date} min={addDays(todayStr(), -30)} max={maxDate}
                       onChange={e => e.target.value && goDate(e.target.value)} aria-label="Elegir fecha" />
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {loading && <Spinner animation="border" size="sm" style={{ alignSelf: 'center' }} />}
                    {access.canManage && <Link to="/salas/qr" className="sl-btn sl-btn-ghost">{I.qr}<span>Códigos QR</span></Link>}
                    <button className="sl-btn sl-btn-primary" onClick={openNew}>{I.plus}<span>Nueva reserva</span></button>
                </div>
            </div>

            {!dayOpen && <div className="sl-alert warn">Este día no se reservan salas.</div>}
            {isPastDay && dayOpen && <div className="sl-alert info">Estás viendo un día pasado. Solo consulta.</div>}

            <div className="sl-tabs">
                {rooms.map(r => (
                    <button key={r._id} className={`sl-tab ${String(activeRoom) === String(r._id) ? 'active' : ''}`}
                            style={{ '--tab-color': r.color }} onClick={() => setActiveRoom(r._id)}>{r.name}</button>
                ))}
            </div>

            <div className="sl-grid-wrap">
                <div className="sl-grid sl-grid-head" style={{ '--cols': rooms.length }}>
                    <div className="sl-cell" />
                    {rooms.map(r => (
                        <div key={r._id} className={`sl-cell ${String(activeRoom) === String(r._id) ? '' : 'hidden-m'}`}>
                            <span className="sl-room-dot" style={{ background: r.color }} />{r.name}
                            <small>{r.capacity} personas{r.location ? ` · ${r.location}` : ''}</small>
                        </div>
                    ))}
                </div>
                <div className="sl-grid" style={{ '--cols': rooms.length }}>
                    <div className="sl-times">
                        {slots.map(m => <div key={m} className="sl-time">{m % 60 === 0 || settings.slotMinutes >= 60 ? fromMins(m) : ''}</div>)}
                    </div>
                    {rooms.map(r => {
                        const list = byRoom.get(String(r._id)) || [];
                        return (
                            <div key={r._id} className={`sl-col ${String(activeRoom) === String(r._id) ? '' : 'hidden-m'}`}>
                                {slots.map(m => {
                                    const past = isPastDay || (isToday && m + settings.slotMinutes <= nowMin);
                                    // Bloque ya tomado (total o parcialmente): no se puede seleccionar
                                    const taken = list.some(b => {
                                        const s = new Date(b.start), e = new Date(b.end);
                                        return m < e.getHours() * 60 + e.getMinutes() && m + settings.slotMinutes > s.getHours() * 60 + s.getMinutes();
                                    });
                                    const cls = ['sl-slot', m % 60 === 0 ? 'hour' : '', !dayOpen ? 'closed' : past ? 'past' : taken ? 'busy' : ''].join(' ');
                                    const free = !past && dayOpen && !taken;
                                    return <div key={m} className={cls} onClick={() => free && openForm(r._id, m)} title={free ? `Reservar ${r.name} a las ${fromMins(m)}` : taken ? 'Ocupado' : ''} />;
                                })}
                                {list.map(b => {
                                    const s = new Date(b.start), e = new Date(b.end);
                                    const sm = Math.max(openMin, s.getHours() * 60 + s.getMinutes());
                                    const em = Math.min(closeMin, e.getHours() * 60 + e.getMinutes());
                                    if (em <= sm) return null;
                                    const who = b.bookedBy?.name || '';
                                    return (
                                        <div key={b._id} className={`sl-evt ${b.isMine ? 'mine' : ''}`}
                                             style={{ top: pct(sm), height: `calc(${((em - sm) / total) * 100}% - 2px)`, '--evt-color': r.color, '--evt-bg': `${r.color}1f` }}
                                             onClick={() => setModal({ type: 'detail', booking: b })}>
                                            <b>{b.title}</b>
                                            <span>{hm(s)}–{hm(e)} · {who}{b.releasedAt ? ' · liberada' : ''}</span>
                                        </div>
                                    );
                                })}
                                {isToday && nowMin >= openMin && nowMin <= closeMin && <div className="sl-now" style={{ top: pct(nowMin) }} />}
                            </div>
                        );
                    })}
                </div>
            </div>

            <section className="sl-section">
                <h3>Mis próximas reservas</h3>
                {mine.length === 0 && <div className="sl-card sl-empty">No tienes reservas próximas.</div>}
                <div className="sl-list">
                    {mine.map(b => {
                        const s = new Date(b.start);
                        return (
                            <div key={b._id} className="sl-item" role="button" onClick={() => setModal({ type: 'detail', booking: b })} style={{ cursor: 'pointer' }}>
                                <div className="sl-item-date"><b>{s.getDate()}</b><small>{MONTHS[s.getMonth()]}</small></div>
                                <div className="sl-item-body">
                                    <b>{b.title}</b>
                                    <span><span className="sl-room-dot" style={{ background: b.room?.color }} />{b.room?.name} · {fmtShort(s)} · {hm(b.start)}–{hm(b.end)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {modal?.type === 'form' && (
                <BookingForm initial={modal.initial} booking={modal.booking} rooms={rooms} settings={settings}
                             onClose={() => setModal(null)}
                             onSaved={(b) => { setModal(null); load(true); if (b?.start) { const d = toStr(new Date(b.start)); if (d !== date) goDate(d); } }} />
            )}
            {modal?.type === 'detail' && (
                <BookingDetail booking={modal.booking} canManage={access.canManage} meId={me.user.id}
                               onClose={() => setModal(null)}
                               onEdit={(b) => setModal({ type: 'form', booking: b, initial: { room: b.room?._id || b.room, date: toStr(new Date(b.start)), start: hm(b.start), end: hm(b.end) } })}
                               onChanged={() => { setModal(null); load(true); }} />
            )}
        </>
    );
}

// ── Disponibilidad ───────────────────────────────────────────────────────────
/** Intervalos ocupados [s,e) en minutos del día para una lista de reservas,
 *  recortados a la ventana de apertura. */
function busyIntervals(list, openMin, closeMin, excludeId) {
    return list
        .filter(b => b.status !== 'cancelled' && b._id !== excludeId)
        .map(b => {
            const s = new Date(b.start), e = new Date(b.end);
            return { s: Math.max(openMin, s.getHours() * 60 + s.getMinutes()), e: Math.min(closeMin, e.getHours() * 60 + e.getMinutes()), title: b.title, who: b.bookedBy?.name };
        })
        .filter(x => x.e > x.s)
        .sort((a, b) => a.s - b.s);
}
const inBusy   = (m, busy) => busy.some(x => m >= x.s && m < x.e);
const overlaps = (s, e, busy) => busy.some(x => s < x.e && e > x.s);
/** Minutos libres a partir de `s` (hasta la siguiente reserva o el cierre). */
const freeFrom = (s, busy, closeMin) => { const nx = busy.find(x => x.s >= s); return (nx ? nx.s : closeMin) - s; };

/** Hueco libre más cercano a `want` con al menos `minLen` minutos; devuelve
 *  { s, e } con e = min(want+len, lo que haya libre) o null si no hay nada. */
function suggestSlot(want, len, busy, openMin, closeMin, step, minLen, notBefore) {
    let best = null;
    for (let s = openMin; s + minLen <= closeMin; s += step) {
        if (s < notBefore || inBusy(s, busy)) continue;
        const free = freeFrom(s, busy, closeMin);
        if (free < minLen) continue;
        const dist = Math.abs(s - want);
        if (!best || dist < best.dist || (dist === best.dist && s > best.s)) best = { s, e: s + Math.min(len, free), dist };
    }
    return best;
}

// ── Formulario crear / editar / mover ────────────────────────────────────────
function BookingForm({ initial, booking, rooms, settings, onClose, onSaved }) {
    const editing = !!booking;
    const [room, setRoom]   = useState(String(initial.room || rooms[0]?._id));
    const [date, setDate]   = useState(initial.date);
    const [start, setStart] = useState(initial.start);
    const [end, setEnd]     = useState(initial.end);
    const [title, setTitle] = useState(booking?.title || '');
    const [attendees, setAttendees] = useState(booking?.attendees || '');
    const [notes, setNotes] = useState(booking?.notes || '');
    const [err, setErr] = useState('');
    const [info, setInfo] = useState('');
    const [saving, setSaving] = useState(false);
    const [dayList, setDayList] = useState(null); // reservas de esa sala ese día
    const { openMin, closeMin } = useSlots(settings);

    const step   = settings.slotMinutes;
    const minLen = Math.max(step, settings.minDurationMin || 15);
    const maxLen = settings.maxDurationMin || 480;
    const isToday = date === todayStr();
    const nowM = new Date().getHours() * 60 + new Date().getMinutes();
    // Para hoy, no se puede empezar en un bloque que ya pasó
    const notBefore = isToday ? Math.ceil(nowM / step) * step : openMin;

    // Cargar la agenda de la sala/fecha elegidas para saber qué está ocupado
    useEffect(() => {
        let alive = true;
        setDayList(null);
        api.get('/salas/bookings', { params: { from: date, to: date, room } })
            .then(({ data }) => alive && setDayList(data))
            .catch(() => alive && setDayList([]));
        return () => { alive = false; };
    }, [room, date]);

    const busy = useMemo(() => busyIntervals(dayList || [], openMin, closeMin, booking?._id), [dayList, openMin, closeMin, booking]);

    // Si el horario elegido cae en algo ocupado (o ya pasó), proponer el hueco
    // libre más cercano en cuanto llega la agenda.
    useEffect(() => {
        if (!dayList) return;
        const s = mins(start), e = mins(end);
        const clash = overlaps(s, e, busy) || s < notBefore;
        if (!clash) { setInfo(''); return; }
        const sug = suggestSlot(s, Math.max(minLen, e - s), busy, openMin, closeMin, step, minLen, notBefore);
        if (sug) {
            setStart(fromMins(sug.s)); setEnd(fromMins(sug.e));
            setInfo(`Ese horario ya está ocupado en esta sala. Te propongo ${fromMins(sug.s)}–${fromMins(sug.e)}.`);
        } else {
            setInfo('Esta sala ya no tiene huecos libres ese día. Prueba otra sala u otro día.');
        }
    }, [dayList]); // eslint-disable-line react-hooks/exhaustive-deps

    // Opciones de inicio: deshabilitadas si están ocupadas o ya pasaron.
    const startOpts = [];
    for (let m = openMin; m + minLen <= closeMin; m += step) {
        startOpts.push({ v: fromMins(m), off: m < notBefore || inBusy(m, busy) || freeFrom(m, busy, closeMin) < minLen });
    }
    // Opciones de fin: hasta donde haya libre desde el inicio (y máximo permitido).
    const sM = mins(start);
    const limit = Math.min(closeMin, sM + freeFrom(sM, busy, closeMin), sM + maxLen);
    const endOpts = [];
    for (let m = sM + step; m <= closeMin; m += step) endOpts.push({ v: fromMins(m), off: m > limit || m - sM < minLen });

    const changeStart = (v) => {
        setStart(v);
        const s = mins(v), len = Math.max(minLen, Math.min(mins(end) - sM, maxLen));
        const lim = Math.min(closeMin, s + freeFrom(s, busy, closeMin), s + maxLen);
        setEnd(fromMins(Math.max(s + minLen, Math.min(s + len, lim))));
        setInfo('');
    };

    const durationMin = mins(end) - mins(start);
    const roomDoc = rooms.find(r => String(r._id) === room);
    const nextFree = busy.length ? suggestSlot(mins(start), Math.max(minLen, durationMin), busy, openMin, closeMin, step, minLen, notBefore) : null;
    const chosenClash = overlaps(mins(start), mins(end), busy);

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!title.trim()) return setErr('Escribe el motivo de la junta.');
        if (chosenClash) return setErr('Ese horario ya está ocupado. Elige uno de los disponibles.');
        const payload = {
            room, title: title.trim(), notes, attendees: attendees === '' ? null : Number(attendees),
            start: toDate(date, start).toISOString(), end: toDate(date, end).toISOString(),
        };
        setSaving(true);
        try {
            const res = editing ? await api.patch(`/salas/bookings/${booking._id}`, payload) : await api.post('/salas/bookings', payload);
            toast.success(editing ? 'Reservación actualizada' : `${roomDoc?.name || 'Sala'} reservada`);
            onSaved(res.data);
        } catch (ex) {
            setErr(ex.response?.data?.error || 'No se pudo guardar');
        } finally { setSaving(false); }
    };

    return (
        <Modal title={editing ? 'Editar reservación' : 'Nueva reservación'} onClose={onClose}>
            <form onSubmit={submit}>
                {err && <div className="sl-alert err">{err}</div>}
                {info && !err && <div className="sl-alert info">{info}</div>}
                <div className="sl-field">
                    <label className="sl-label">Sala</label>
                    <select className="sl-select" value={room} onChange={e => { setRoom(e.target.value); setInfo(''); }}>
                        {rooms.map(r => <option key={r._id} value={r._id}>{r.name} · {r.capacity} personas{r.location ? ` · ${r.location}` : ''}</option>)}
                    </select>
                </div>
                <div className="sl-field">
                    <label className="sl-label">Fecha</label>
                    <input type="date" className="sl-input" value={date} min={todayStr()} max={addDays(todayStr(), settings.maxDaysAhead || 60)} onChange={e => { if (e.target.value) { setDate(e.target.value); setInfo(''); } }} required />
                </div>
                <div className="sl-row2">
                    <div className="sl-field">
                        <label className="sl-label">Inicio</label>
                        <select className="sl-select" value={start} onChange={e => changeStart(e.target.value)} disabled={!dayList}>
                            {!startOpts.some(o => o.v === start) && <option value={start}>{start}</option>}
                            {startOpts.map(o => <option key={o.v} value={o.v} disabled={o.off}>{o.v}{o.off ? '  · ocupado' : ''}</option>)}
                        </select>
                    </div>
                    <div className="sl-field">
                        <label className="sl-label">Fin</label>
                        <select className="sl-select" value={end} onChange={e => { setEnd(e.target.value); setInfo(''); }} disabled={!dayList}>
                            {!endOpts.some(o => o.v === end) && <option value={end}>{end}</option>}
                            {endOpts.map(o => <option key={o.v} value={o.v} disabled={o.off}>{o.v}{o.off && mins(o.v) > limit ? '  · no disponible' : ''}</option>)}
                        </select>
                    </div>
                </div>
                <div className="sl-hint" style={{ marginTop: -6, marginBottom: 10 }}>
                    Duración: {durationMin >= 60 ? `${Math.floor(durationMin / 60)} h${durationMin % 60 ? ` ${durationMin % 60} min` : ''}` : `${durationMin} min`}
                    {settings.maxDurationMin ? ` · máximo ${Math.round(settings.maxDurationMin / 60)} h` : ''}
                    {!dayList && ' · consultando disponibilidad…'}
                </div>
                {chosenClash && nextFree && (
                    <div className="sl-alert warn" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ flex: 1 }}>Ese horario ya está ocupado.</span>
                        <button type="button" className="sl-btn sl-btn-primary sm" onClick={() => { setStart(fromMins(nextFree.s)); setEnd(fromMins(nextFree.e)); setInfo(''); }}>
                            Usar {fromMins(nextFree.s)}–{fromMins(nextFree.e)}
                        </button>
                    </div>
                )}
                {dayList && busy.length > 0 && (
                    <div className="sl-field">
                        <label className="sl-label">Ocupado ese día en {roomDoc?.name}</label>
                        <div className="sl-chips">
                            {busy.map((x, i) => <span key={i} className="sl-chip" title={x.title}>{I.clock} {fromMins(x.s)}–{fromMins(x.e)} · {x.who}</span>)}
                        </div>
                    </div>
                )}
                <div className="sl-field">
                    <label className="sl-label">Motivo de la junta</label>
                    <input className="sl-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej. Comité de crédito" maxLength={120} required autoFocus={!editing} />
                </div>
                <div className="sl-field">
                    <label className="sl-label">Asistentes <span className="sl-muted">(opcional)</span></label>
                    <input type="number" min="1" max="500" className="sl-input" value={attendees} onChange={e => setAttendees(e.target.value)} placeholder={roomDoc ? `hasta ${roomDoc.capacity}` : ''} />
                </div>
                {roomDoc && attendees && Number(attendees) > roomDoc.capacity && (
                    <div className="sl-alert warn">La {roomDoc.name} es para {roomDoc.capacity} personas.</div>
                )}
                <div className="sl-field">
                    <label className="sl-label">Notas <span className="sl-muted">(opcional)</span></label>
                    <textarea className="sl-textarea" value={notes} onChange={e => setNotes(e.target.value)} maxLength={500} placeholder="Equipo necesario, invitados externos, etc." />
                </div>
                <div className="sl-modal-foot">
                    <button type="button" className="sl-btn sl-btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
                    <button type="submit" className="sl-btn sl-btn-primary" disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : editing ? 'Guardar cambios' : 'Reservar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// ── Detalle de una reservación ───────────────────────────────────────────────
function BookingDetail({ booking: b, canManage, meId, onClose, onEdit, onChanged }) {
    const [confirm, setConfirm] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [hist, setHist] = useState(null);
    const isMine = b.isMine || String(b.bookedBy?._id) === String(meId);
    const now = new Date();
    const ended  = new Date(b.end) <= now;
    const inProgress = new Date(b.start) <= now && !ended;
    const canEdit = canManage || (isMine && !ended);
    const who = b.bookedBy?.name;
    const [confirmRelease, setConfirmRelease] = useState(false);

    const release = async () => {
        setBusy(true);
        try {
            await api.post(`/salas/bookings/${b._id}/release`);
            toast.success(`${b.room?.name || 'Sala'} liberada: ya puede reservarse`);
            onChanged();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo liberar'); }
        finally { setBusy(false); }
    };

    const cancel = async () => {
        setBusy(true);
        try {
            await api.delete(`/salas/bookings/${b._id}`, { data: { reason } });
            toast.success('Reservación cancelada');
            onChanged();
        } catch (e) { toast.error(e.response?.data?.error || 'No se pudo cancelar'); }
        finally { setBusy(false); }
    };
    const loadHist = async () => {
        try { const { data } = await api.get(`/salas/bookings/${b._id}/history`); setHist(data.history || []); }
        catch { toast.error('No se pudo cargar el historial'); }
    };
    const ACTION = { created: 'Creada', moved: 'Movida', updated: 'Editada', cancelled: 'Cancelada', released: 'Liberada antes' };

    return (
        <Modal title={b.title} onClose={onClose}>
            <dl className="sl-detail">
                <dt>Sala</dt><dd><span className="sl-room-dot" style={{ background: b.room?.color }} />{b.room?.name}</dd>
                <dt>Cuándo</dt><dd style={{ textTransform: 'capitalize' }}>{fmtShort(b.start)} · {hm(b.start)}–{hm(b.end)}</dd>
                <dt>Reservó</dt><dd>{who}{b.bookedBy?.puesto ? <span className="sl-muted"> · {b.bookedBy.puesto}</span> : null}</dd>
                {b.attendees && <><dt>Asistentes</dt><dd>{b.attendees}</dd></>}
                {b.notes && <><dt>Notas</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{b.notes}</dd></>}
                {b.releasedAt && <><dt>Liberada</dt><dd>a las {hm(b.releasedAt)} por {b.releasedBy?.name || '—'} <span className="sl-muted">(estaba apartada hasta {hm(b.originalEnd || b.end)})</span></dd></>}
            </dl>
            {inProgress && !b.releasedAt && canEdit && (
                <div className="sl-alert info" style={{ marginTop: 12, marginBottom: 0 }}>
                    Esta junta está en curso. Si ya terminó, libera la sala para que el resto del tiempo quede disponible.
                </div>
            )}

            {hist && (
                <div className="sl-card" style={{ marginTop: 14, padding: '10px 14px' }}>
                    {hist.map((h, i) => (
                        <div key={i} className="sl-muted" style={{ padding: '3px 0' }}>
                            <b style={{ color: 'var(--c-text-1)' }}>{ACTION[h.action] || h.action}</b> · {h.by?.name || '—'} · {new Date(h.at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                            {h.action === 'moved' && h.from?.start && <> · antes {h.from.room?.name || ''} {hm(h.from.start)}–{hm(h.from.end)}</>}
                        </div>
                    ))}
                </div>
            )}

            {confirmRelease ? (
                <div className="sl-alert warn" style={{ marginTop: 14 }}>
                    <b>¿Liberar {b.room?.name || 'la sala'} ahora?</b> La reserva terminará a las {hm(new Date())} en vez de las {hm(b.end)} y el resto del tiempo quedará disponible.
                    <div className="sl-modal-foot" style={{ marginTop: 10 }}>
                        <button className="sl-btn sl-btn-ghost sm" onClick={() => setConfirmRelease(false)} disabled={busy}>No</button>
                        <button className="sl-btn sl-btn-primary sm" onClick={release} disabled={busy}>{busy ? '…' : 'Sí, liberar'}</button>
                    </div>
                </div>
            ) : confirm ? (
                <div className="sl-alert warn" style={{ marginTop: 14 }}>
                    <b>¿Cancelar esta reservación?</b>
                    {canManage && !isMine && (
                        <input className="sl-input" style={{ marginTop: 8 }} placeholder="Motivo (se guarda en el historial)" value={reason} onChange={e => setReason(e.target.value)} maxLength={300} />
                    )}
                    <div className="sl-modal-foot" style={{ marginTop: 10 }}>
                        <button className="sl-btn sl-btn-ghost sm" onClick={() => setConfirm(false)} disabled={busy}>No</button>
                        <button className="sl-btn sl-btn-danger sm" onClick={cancel} disabled={busy}>{busy ? '…' : 'Sí, cancelar'}</button>
                    </div>
                </div>
            ) : (
                <div className="sl-modal-foot">
                    {canManage && !hist && <button className="sl-btn sl-btn-ghost sm" onClick={loadHist}>{I.hist} Historial</button>}
                    {inProgress && !b.releasedAt && canEdit && <button className="sl-btn sl-btn-primary sm" onClick={() => setConfirmRelease(true)}>{I.room} Liberar sala</button>}
                    {canEdit && <button className="sl-btn sl-btn-danger sm" onClick={() => setConfirm(true)}>{I.trash} Cancelar reserva</button>}
                    {canEdit && <button className="sl-btn sl-btn-primary sm" onClick={() => onEdit(b)}>{I.edit} Editar / mover</button>}
                    {!canEdit && <button className="sl-btn sl-btn-ghost sm" onClick={onClose}>Cerrar</button>}
                </div>
            )}
        </Modal>
    );
}

// ── Piezas ───────────────────────────────────────────────────────────────────
function Modal({ title, children, onClose }) {
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div className="sl-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="sl-modal" role="dialog" aria-modal="true">
                <div className="sl-modal-head">
                    <h2>{title}</h2>
                    <button className="sl-btn sl-btn-ghost icon" onClick={onClose} aria-label="Cerrar">{I.x}</button>
                </div>
                {children}
            </div>
        </div>
    );
}

/** Autocompletar de usuarios del Desk; si no se elige ninguno, queda el texto libre. */
export function UserPicker({ value, text, onPick, onText, placeholder = 'Nombre (busca o escribe)', clearOnPick = false }) {
    const [q, setQ] = useState(value?.name || text || '');
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const t = useRef(null);
    useEffect(() => { setQ(value?.name || text || ''); }, [value, text]);

    const change = (v) => {
        setQ(v); onText(v);
        clearTimeout(t.current);
        if (v.trim().length < 2) { setItems([]); return; }
        t.current = setTimeout(async () => {
            try { const { data } = await api.get('/salas/users', { params: { q: v.trim() } }); setItems(data); setOpen(true); }
            catch { setItems([]); }
        }, 250);
    };
    return (
        <div className="sl-ac">
            <input className="sl-input" value={q} onChange={e => change(e.target.value)} onFocus={() => items.length && setOpen(true)}
                   onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={placeholder} maxLength={120} />
            {open && items.length > 0 && (
                <div className="sl-ac-list">
                    {items.map(u => (
                        <div key={u._id} className="sl-ac-item" onMouseDown={() => { onPick(u); setOpen(false); setItems([]); if (clearOnPick) setQ(''); }}>
                            {u.name}<small>{u.puesto || u.email}</small>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
